import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useAuthStore } from "@/store/authStore";
import { demoCourses, demoWorkLogs, demoPlacements } from "@/data/demoData";
import { unwrapApiArray, unwrapApiObject } from "@/lib/api-response";

type NormalizedCourse = {
  id: string | number;
  title: string;
  code: string;
  description: string;
  status: string;
  instructor: string;
  duration: string;
  enrollments: number;
  fee: number;
  category: string;
  type: string;
  image: string;
};

// Mirrors the StudentProfile payload from GET /students/me. Every field is
// optional because the backend creates an empty profile on first read, so
// consumers must supply their own fallback.
export type StudentProfileDTO = {
  id?: number;
  user_id?: number;
  student_id?: string;
  first_name?: string;
  last_name?: string;
  bio?: string;
  university?: string;
  major?: string;
  year_of_study?: number;
  skills?: string;
  resume_url?: string;
  dean_letter_url?: string;
  student_id_url?: string;
  phone?: string;
  gender?: string;
  address?: string;
  created_at?: string;
  updated_at?: string;
};

function normalizeCourse(course: Record<string, unknown>): NormalizedCourse {
  const title = String(course.title || course.name || course.code || "Untitled course");
  const fee =
    typeof course.fee === "number"
      ? course.fee
      : typeof course.price === "number"
        ? course.price
        : 0;
  const category =
    course.category ||
    course.type ||
    (fee > 0
      ? "Premium Course"
      : title.toLowerCase().includes("orientation")
        ? "Orientation"
        : "Free Course");

  return {
    id: String(course.id || title),
    title,
    code: String(course.code || ""),
    description: String(course.description || "Practical learning experience."),
    status: String(course.status || "available"),
    instructor: String(course.instructor || "Taskpart Learning Team"),
    duration: String(course.duration || "Self-paced"),
    enrollments: Number(course.enrollments || course.students || 0),
    fee,
    category: String(category),
    type: String(
      course.type || (fee > 0 ? "paid" : category === "Orientation" ? "orientation" : "free"),
    ),
    image: String(
      course.image ||
        "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=80",
    ),
  };
}

export function useStudentCourses() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["student-courses"],
    queryFn: async () => {
      if (user?.isDemo) {
        return demoCourses.map(normalizeCourse);
      }
      try {
        const res = await apiClient.get("/courses");
        const items = unwrapApiArray<Record<string, unknown>>(res);
        return items.map(normalizeCourse);
      } catch (error) {
        console.error("Failed to fetch student courses:", error);
        throw error;
      }
    },
  });
}

export function useEnrollCourse() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (courseId: string | number) => {
      if (user?.isDemo) {
        return { success: true, message: "Enrolled (Demo Mode)" };
      }
      const res = await apiClient.post(`/courses/${courseId}/enroll`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-courses"] });
      queryClient.invalidateQueries({ queryKey: ["student-enrollments"] });
    },
  });
}

export function useStudentEnrollments() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["student-enrollments"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/students/me/enrollments");
        return unwrapApiArray(res);
      } catch (error) {
        console.error("Failed to fetch student enrollments:", error);
        return [];
      }
    },
  });
}

export function useCreatePaymentIntent() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    // No `amount`: the API prices the purchase from the admin-controlled
    // pricing settings, so sending a figure from the client would only
    // misrepresent the chargeable amount.
    mutationFn: async (data: {
      currency: string;
      type: string;
      payment_method: string;
      phone_number?: string;
      reference_id?: string;
      return_path?: string;
    }) => {
      if (user?.isDemo) {
        return { success: true, message: "Payment initiated (Demo Mode)" };
      }
      const res = await apiClient.post("/students/me/payments/intents", data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-payments"] });
    },
  });
}

export function useStudentPayments() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["student-payments"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/students/me/payments");
        return unwrapApiArray(res);
      } catch (error) {
        console.error("Failed to fetch student payments:", error);
        throw error;
      }
    },
  });
}

/**
 * A work log as the API returns it.
 *
 * The API serves `description`/`challenges`/`partner_name`, and the detail
 * screens read those names. This used to be untyped, so the screens happily read
 * `log.description` and `log.partnerName` from a payload that carried `content`
 * and no partner at all, and rendered blanks instead of failing.
 */
export interface StudentWorkLog {
  id: number | string;
  placement_id?: number;
  date: string;
  hours: number;
  description: string;
  content?: string;
  challenges?: string;
  challenge?: string;
  status: string;
  feedback?: string;
  partner_name?: string;
  partnerName?: string;
  slot_title?: string;
  reviewed_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export function useStudentWorkLogsList() {
  const { user } = useAuthStore();

  return useQuery<StudentWorkLog[]>({
    queryKey: ["student-work-logs-list"],
    queryFn: async (): Promise<StudentWorkLog[]> => {
      if (user?.isDemo) {
        return demoWorkLogs as unknown as StudentWorkLog[];
      }
      try {
        const res = await apiClient.get("/students/me/work-logs");
        return unwrapApiArray<StudentWorkLog>(res);
      } catch (error) {
        console.error("Failed to fetch student work logs:", error);
        throw error;
      }
    },
  });
}

/** One work log, read straight from its own endpoint rather than filtered out of the list. */
export function useStudentWorkLog(id: string) {
  const { user } = useAuthStore();

  return useQuery<StudentWorkLog | null>({
    queryKey: ["student-work-log", id],
    queryFn: async (): Promise<StudentWorkLog | null> => {
      if (user?.isDemo) {
        const found = demoWorkLogs.find((l) => String(l.id) === String(id));
        return found ? (found as unknown as StudentWorkLog) : null;
      }
      const res = await apiClient.get(`/students/me/work-logs/${id}`);
      return unwrapApiObject(res, null as any) as StudentWorkLog | null;
    },
    enabled: !!id,
  });
}

export function useUpdateStudentWorkLog() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      id: string;
      date: string;
      hours: number;
      description: string;
      challenge?: string;
    }) => {
      if (user?.isDemo) {
        return { success: true, message: "Work log updated (Demo Mode)" };
      }
      const { id, ...body } = data;
      const res = await apiClient.patch(`/students/me/work-logs/${id}`, body);
      return res.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["student-work-logs-list"] });
      queryClient.invalidateQueries({ queryKey: ["student-work-log", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["student-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["student-placements"] });
    },
  });
}

export function useDeleteStudentWorkLog() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (id: string) => {
      if (user?.isDemo) {
        return { success: true, message: "Work log deleted (Demo Mode)" };
      }
      const res = await apiClient.delete(`/students/me/work-logs/${id}`);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-work-logs-list"] });
      queryClient.invalidateQueries({ queryKey: ["student-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["student-placements"] });
    },
  });
}

export function useStudentPlacement() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["student-placement"],
    queryFn: async () => {
      if (user?.isDemo) {
        return demoPlacements[0] || null;
      }
      try {
        const res = await apiClient.get("/students/me/placement");
        return unwrapApiObject(res, null as any);
      } catch (error) {
        console.error("Failed to fetch student placement:", error);
        throw error;
      }
    },
  });
}

export function usePlacementOpportunities() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["placement-opportunities"],
    queryFn: async () => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/students/me/placement/opportunities");
        return unwrapApiArray(res);
      } catch (error) {
        // Returning [] would render identically to "no opportunities", so a
        // 401/403/500 would be indistinguishable from an empty result set.
        console.error("Failed to fetch placement opportunities:", error);
        throw error;
      }
    },
  });
}

export function useUpdateOrientation() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: { module: string; status: boolean }) => {
      if (user?.isDemo) {
        return { success: true };
      }
      const res = await apiClient.post("/students/me/orientation/modules", data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-placement"] });
    },
  });
}

export type StudentCertificate = {
  id: string | number;
  student_name?: string;
  course?: string;
  university?: string;
  issue_date?: string;
  cert_number?: string;
  template_id?: number;
  type?: string;
  badge_icon?: string;
  source_type?: string;
  score_percent?: number;
  hours_earned?: number;
  course_id?: number;
  placement_id?: number;
};

export function useStudentCertificates() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["student-certificates"],
    queryFn: async (): Promise<StudentCertificate[]> => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get(`/certificates/user/${user?.id}`);
        return unwrapApiArray<StudentCertificate>(res);
      } catch (error) {
        console.error("Failed to fetch student certificates:", error);
        throw error;
      }
    },
    enabled: !!user?.id,
  });
}

export function useSubmitStudentWorkLog() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: {
      placement_id: number;
      date: string;
      hours: number;
      description: string;
      challenge?: string;
    }) => {
      if (user?.isDemo) {
        return { success: true, message: "Work log submitted (Demo Mode)" };
      }
      const res = await apiClient.post("/students/me/work-logs", data);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["student-work-logs-list"] });
      queryClient.invalidateQueries({ queryKey: ["student-placements"] });
    },
  });
}

/** One of the student's own active placements. */
export interface StudentPlacementOption {
  id: number;
  status: string;
  slot_title: string;
  partner_name: string;
  location: string;
  start_date?: string;
  end_date?: string;
  hours_required?: number;
  hours_logged?: number;
}

/**
 * Every active placement the student holds. A student can be on more than one
 * partner at a time, and work log hours are reviewed by the partner they belong
 * to, so the chooser needs the whole set rather than whichever placement the
 * singular endpoint happens to return.
 */
export function useStudentPlacements() {
  const { user } = useAuthStore();

  return useQuery<StudentPlacementOption[]>({
    queryKey: ["student-placements"],
    queryFn: async (): Promise<StudentPlacementOption[]> => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/students/me/placement/placements");
        return unwrapApiArray<StudentPlacementOption>(res);
      } catch (error) {
        console.error("Failed to fetch student placements:", error);
        return [];
      }
    },
    enabled: !user?.isDemo,
  });
}

export function useStudentProfile() {
  const { user } = useAuthStore();

  return useQuery<StudentProfileDTO>({
    queryKey: ["student-profile"],
    queryFn: async (): Promise<StudentProfileDTO> => {
      if (user?.isDemo) {
        return {
          first_name: user.name || "",
          university: user.university || "UDSM",
        };
      }
      try {
        const res = await apiClient.get("/students/me");
        return unwrapApiObject<StudentProfileDTO>(res, {});
      } catch (error) {
        console.error("Failed to fetch student profile:", error);
        throw error;
      }
    },
  });
}

export function useUpdateStudentProfile() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (user?.isDemo) {
        return { ...data, success: true };
      }
      const res = await apiClient.patch("/students/me", data);
      return res.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["student-profile"] });
      if (data && !user?.isDemo) {
        const fullName = `${data.first_name} ${data.last_name}`;
        useAuthStore.getState().setUser({ ...useAuthStore.getState().user!, name: fullName });
      }
    },
  });
}

/** One of the student's own applications to a published work slot. */
export interface StudentPlacementRequest {
  id: number;
  slot_id: number;
  status: "submitted" | "accepted" | "declined" | "withdrawn";
  note?: string;
  slot_title?: string;
  company?: string;
  location?: string;
  duration?: string;
  created_at?: string;
  decided_at?: string | null;
}

/**
 * Applications the student has made, including the ones already decided. The
 * API keeps the full history so a declined application does not silently
 * disappear, but only submitted/accepted count against the free-tier cap.
 */
export function useMyPlacementRequests() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ["my-placement-requests"],
    queryFn: async (): Promise<StudentPlacementRequest[]> => {
      if (user?.isDemo) {
        return [];
      }
      try {
        const res = await apiClient.get("/students/me/placement/requests");
        return unwrapApiArray<StudentPlacementRequest>(res);
      } catch (error) {
        console.error("Failed to fetch placement requests:", error);
        throw error;
      }
    },
  });
}

export function useApplyToSlot() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (input: { slot_id: number; note?: string }) => {
      if (user?.isDemo) {
        return { success: true };
      }
      const res = await apiClient.post("/students/me/placement/requests", input);
      return unwrapApiObject(res, null as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-placement-requests"] });
    },
  });
}

export function useWithdrawPlacementRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  return useMutation({
    mutationFn: async (requestId: number) => {
      if (user?.isDemo) {
        return { success: true };
      }
      const res = await apiClient.post(`/students/me/placement/requests/${requestId}/withdraw`, {});
      return unwrapApiObject(res, null as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-placement-requests"] });
    },
  });
}
